import { diff, DiffChange, getPath, setPath, deepFreeze } from "@spscommerce/utils";
import { useCallback, useMemo, useReducer, useRef } from "react";
import nanoid from "nanoid";
// lodash packages must be imported this way to work with ng-packagr
import * as isPlainObjectImport from "lodash.isplainobject";

import { SpsValidator, SpsValidators, SpsValidationErrors, validate } from "../SpsValidators";

const isPlainObject = (<any>isPlainObjectImport).default || isPlainObjectImport;

type PathComponent = string | number;
type Path = Array<PathComponent>;
type UpdateFn = (path?: Path, newValue?: any) => void;

// Base class for all form metadata structures: controls, groups, and arrays
export abstract class SpsFormMetaBase<T> {
    validators: Array<SpsValidator<T>> = [];
    errors: SpsValidationErrors | null = null;

    constructor(
        public path: Path,
        public update: UpdateFn,
    ) {}

    abstract isPristine(): boolean;
    abstract markAsPristine(): SpsFormMetaBase<T>;
    abstract markAsDirty(): SpsFormMetaBase<T>;
    abstract isFocused(): boolean;

    setValidators(newValidators: Array<SpsValidator<T>>): SpsFormMetaBase<T> {
        this.validators = newValidators;
        this.update(null);
        return this;
    }

    validate(newValue: T): SpsFormMetaBase<T> {
        if (this.validators && this.validators.length > 0) {
            this.errors = validate(newValue, this.validators);
            this.update(null);
            if (this.isFocused() && this.onFocus) {
                this.onFocus();
            }
        } else if (this.errors) {
            this.errors = null;
            this.update(null);
        }
        return this;
    }

    isValid() {
        return !this.errors || this.isPristine();
    }

    hasError(errorKey: string) {
        return this.errors && this.errors.hasOwnProperty(errorKey);
    }

    isRequired() {
        return this.validators && this.validators.indexOf(SpsValidators.required) > -1;
    }

    onFocus() {}
    onBlur() {}
}

// Metadata for an individual control such as a text input or checkbox
export class SpsFormFieldMeta<T> extends SpsFormMetaBase<T> {
    id: string = nanoid();
    focused = false;
    pristine = true;

    setValue(newValue: T) {
        this.update(this.path, newValue);
    }

    isPristine() {
        return this.pristine;
    }

    markAsPristine(): SpsFormFieldMeta<T> {
        this.pristine = true;
        this.update(null);
        return this;
    }

    markAsDirty(): SpsFormFieldMeta<T> {
        this.pristine = false;
        this.update(null);
        return this;
    }

    isFocused() {
        return this.focused;
    }

    markAsFocused(): SpsFormFieldMeta<T> {
        this.focused = true;
        this.update(null);
        if (this.onFocus) {
            this.onFocus();
        }
        return this;
    }

    markAsBlurred(): SpsFormFieldMeta<T> {
        this.focused = false;
        this.update(null);
        if (this.onBlur) {
            this.onBlur();
        }
        return this;
    }
}

export type InferredSpsFormMeta<T> = SpsFormMetaBase<T> & (
    T extends Array<any>
    ? SpsFormArrayMeta<T>
    : T extends (Array<infer U> | undefined)
    ? SpsFormArrayMeta<Array<U>> | undefined
    : T extends object
    ? SpsFormGroupMeta<T>
    : T extends (object | undefined)
    ? SpsFormGroupMeta<object & T> | undefined
    : T extends (infer V | undefined)
    ? SpsFormFieldMeta<V> | undefined
    : SpsFormFieldMeta<T>
);

export type SpsFormSetMembers<T> = {
    [K in keyof T]?: SpsFormMetaBase<any>;
};

export type InferredSpsFormMetaSetMembers<T> = SpsFormSetMembers<T> & {
    [K in keyof T]: InferredSpsFormMeta<T[K]>;
};

export type SpsFormSetValue = Array<any> | object;

// Base class for form metadata structures that contain other form metadata
// structures: arrays and groups, which have "fields" that can be other
// form arrays or form groups, or form controls (i.e. any SpsFormMetaBase)
export abstract class SpsFormSetMeta<
    T extends SpsFormSetValue,
    U extends SpsFormSetMembers<T> = InferredSpsFormMetaSetMembers<T>
> extends SpsFormFieldMeta<T> {
    fields: U;

    constructor(path: Path, update: UpdateFn);
    constructor(inferMembersFromValue: T, path: Path, update: UpdateFn);
    constructor(inferValOrPath: T | Path, pathOrUpdate: Path | UpdateFn, updateWhenInferring?: UpdateFn) {
        const path: Path = (updateWhenInferring ? pathOrUpdate : inferValOrPath) as Path;
        const update = updateWhenInferring || (pathOrUpdate as UpdateFn);
        super(path, update);

        if (updateWhenInferring) {
            this.inferMembers(inferValOrPath as T);
        }
    }

    protected abstract inferMembers(inferFromValue: T);

    protected inferSpsControl(valueOrControl: any, key: PathComponent): SpsFormMetaBase<any> {
        return inferSpsControl(valueOrControl, [...this.path, key], this.update);
    }

    private rollup(methodName: string): boolean {
        return Object.keys(this.fields).reduce(
            (acc, key) => acc && (!this.fields[key][methodName] || this.fields[key][methodName]()),
            true
        );
    }

    isFocused(): boolean {
        return super.isFocused() || this.rollup("isFocused");
    }

    isValid(): boolean {
        return super.isValid() && this.rollup("isValid");
    }

    contentsAreValid(): boolean {
        // The distinction between "isValid" and "contentsAreValid" is that a form group
        // or form array can have a validator attached, which means the group/array in and
        // of itself has a validation status and associated errors. That is "isValid" for
        // a group/array, the same as for a control.
        return this.rollup("isValid") && this.rollup("contentsAreValid");
    }

    isPristine(): boolean {
        return super.isPristine() && this.rollup("isPristine");
    }

    markAsPristine(): this {
        super.markAsPristine();
        for (const key of Object.keys(this.fields)) {
            this.fields[key].markAsPristine();
        }
        return this;
    }

    markAsDirty(): this {
        super.markAsDirty();
        for (const key of Object.keys(this.fields)) {
            this.fields[key].markAsDirty();
        }
        return this;
    }
}

// Ultimately the difference between SpsFormGroupMeta and SpsFormArrayMeta is merely the type
// of their `fields` property: an object or an array.
export class SpsFormGroupMeta<T extends object> extends SpsFormSetMeta<T> {
    protected inferMembers(inferFromValue: T) {
        this.fields = ({} as InferredSpsFormMetaSetMembers<T>);
        for (const key of Object.keys(inferFromValue)) {
            this.fields[key] = this.inferSpsControl(inferFromValue[key], key);
        }
    }
}

export class SpsFormArrayMeta<T extends Array<any>> extends SpsFormSetMeta<T> {
    protected inferMembers(inferFromValue: T) {
        this.fields = (
            inferFromValue.map((item, i) => this.inferSpsControl(item, String(i)))
        ) as any as InferredSpsFormMetaSetMembers<T>;
    }
}

function inferSpsControl(valueOrControl: any, path: Path, update: UpdateFn): InferredSpsFormMeta<any> {
    if (valueOrControl instanceof SpsFormMetaBase) {
        valueOrControl.path = path;
        valueOrControl.update = update;
        return valueOrControl as InferredSpsFormMeta<any>;
    }
    if (Array.isArray(valueOrControl)) {
        return new SpsFormArrayMeta<any>(valueOrControl, path, update);
    }
    if (isPlainObject(valueOrControl)) {
        return new SpsFormGroupMeta<any>(valueOrControl, path, update);
    }
    return new SpsFormFieldMeta<any>(path, update);
}

function getMember(obj: any, path: Path) {
    if (path.length === 0) {
        return obj;
    }
    return getMember(obj.fields[path[0]], path.slice(1, path.length));
}

function getMembers(obj: any, path: Path): Array<SpsFormMetaBase<any>> {
    if (path.length === 0) {
        return [obj];
    }
    const nextPath = path[0];
    path = path.slice(1, path.length);
    if (nextPath === "*") {
        const fieldsArray = Array.isArray(obj.fields)
            ? obj.fields
            : Object.keys(obj.fields).map((k) => obj.fields[k]);
        if (path.length === 0) {
            return fieldsArray;
        }
        return fieldsArray.reduce(
            (arr, member) => [...arr, ...getMembers(member, path)],
            []
        );
    }
    return getMembers(obj.fields[nextPath], path);
}

export interface SpsFormState<T extends SpsFormSetValue> {
    formValue: T;
    formMeta: SpsFormSetMeta<T>;
    updateForm: (newValue?: T) => void;
}

export type SpsValidatorMapValue = Array<SpsValidator<any>> | ((value) => Array<SpsValidator<any>>);

export interface SpsValidatorMap {
    [key: string]: SpsValidatorMapValue;
}

function validatorMapForEach(
    formMeta: SpsFormSetMeta<any>,
    validatorMap: SpsValidatorMap,
    fn: (control: SpsFormMetaBase<any>, validators: SpsValidatorMapValue) => void
) {
    for (const path of Object.keys(validatorMap)) {
        const validators = validatorMap[path];
        for (const control of getMembers(formMeta, path.split("."))) {
            fn(control, validators);
        }
    }
}

function updateValidators(
    value,
    formMeta: SpsFormSetMeta<any>,
    validatorMap: SpsValidatorMap,
    runValidators = false
) {
    validatorMapForEach(formMeta, validatorMap, (control, validators) => {
        control.validators = typeof validators === "function"
            ? validators(value)
            : validators;
        if (runValidators) {
            control.errors = validate(getPath(value, control.path), control.validators);
        }
    });
}

function cloneFormValue(value, path: Path) {
    const next = path[0];
    path = path.slice(1);
    if (Array.isArray(value)) {
        return value.map((x, i) =>
            i === Number(next)
                ? cloneFormValue(x, path)
                : x
        );
    }
    if (isPlainObject(value)) {
        return Object.keys(value)
            .reduce(
                (acc, key) => Object.assign(acc, {
                    [key]: key === next
                        ? cloneFormValue(value[key], path)
                        : value[key]
                }),
                {}
            );
    }
    return value;
}

export function useSpsForm<T extends SpsFormSetValue>(
    value: T,
    validatorMap?: SpsValidatorMap,
): SpsFormState<T> {
    // Do the work of instantiating `formMeta` the first time the hook is run and ONLY
    // the first time. Thereafter we will keep it up to date as we go.
    const formMeta = useMemo(() => {
        deepFreeze(value);
        const controlSet = inferSpsControl(value, [], update) as SpsFormSetMeta<any>;
        if (validatorMap) {
            updateValidators(value, controlSet, validatorMap, true);
        }
        return controlSet as any as SpsFormSetMeta<T>;
    }, []);
    // Stores a timeout id, used for aggregating `updateForm` calls
    // into a single `useReducer` dispatch per cycle
    const scheduledDispatch = useRef<number>();

    // A stable identity for `reducer` is important to avoid constant re-render thrashing
    const reducer = useCallback((
        currentState: SpsFormState<T>,
        action: { path?: Path, value: any }
    ): SpsFormState<T> => {
        let newValue;
        scheduledDispatch.current = null;

        if (action) {
            if (action.path) {
                // This branch is used when an SpsFormFieldMeta's value is updated by
                // its associated component, i.e. when the user interacts with it
                newValue = cloneFormValue(currentState.formValue, action.path);
                setPath(newValue, action.path, action.value);
            } else {
                newValue = action.value;
            }
        }

        if (newValue) {
            deepFreeze(newValue);
            const valueDiff = diff(currentState.formValue, newValue);
            for (const { type: dType, key, parentPath, objects: [oldSubValue, newSubValue] } of valueDiff) {
                const meta: SpsFormSetMeta<any> = getMember(currentState.formMeta, parentPath as Path);

                // I don't expect anyone to have symbol properties on their form value;
                // this is mainly for TypeScript to understand that `key` is a number or string
                // within this block. And if someone does have a symbol-keyed property, I
                // don't know what would be going on there and we'll err on the side of not
                // touching it.
                if (typeof key !== "symbol") {
                    // Update `formMeta` in sync with new fields added or fields removed within the value
                    switch (dType) {
                        case DiffChange.ADDITION:
                            meta.fields[key] = inferSpsControl(newSubValue[key], [...parentPath, key] as Path, update);
                            break;

                        case DiffChange.DELETION:
                            if (Array.isArray(meta.fields)) {
                                meta.fields.splice(+key, 1);
                            } else {
                                delete meta.fields[key];
                            }
                            break;

                        case DiffChange.ALTERATION:
                            if (!oldSubValue[key] && newSubValue[key] && !(meta.fields[key] instanceof SpsFormSetMeta)) {
                                meta.fields[key] = inferSpsControl(newSubValue[key], [...parentPath, key] as Path, update);
                            }
                    }
                }
            }

            if (validatorMap) {
                // The changes may alter the results of dynamic validator configurations (i.e. cases where
                // they are defined as a function that returns conditionally different lists of validators).
                // As well, any items added during the previous step may be included in a wildcard
                // validator config, and therefore need their validators added.
                updateValidators(newValue, currentState.formMeta, validatorMap);

                const alreadyValidated = new Set<SpsFormMetaBase<any>>();
                validatorMapForEach(currentState.formMeta, validatorMap, (control, validators) => {
                    // For any control with dynamic validators, its list of validators might have changed.
                    // So re-run validation and keep track of these so we don't needlessly run them again
                    // in the next step.
                    if (typeof validators === "function") {
                        control.validate(getPath(newValue, control.path));
                        alreadyValidated.add(control);
                    }
                });

                // Targeted validation of changes, excluding things that were already validated in the
                // previous step
                for (const { type: dType, key, parentPath, objects: [, newSubValue] } of valueDiff) {
                    if (typeof key !== "symbol") {
                        const meta: SpsFormSetMeta<any> = getMember(currentState.formMeta, parentPath as Path);
                        // Revalidate the group/array that had something added, removed, or altered
                        if (!alreadyValidated.has(meta)) {
                            meta.validate(newSubValue);
                        }
                        if (dType === DiffChange.ADDITION || dType === DiffChange.ALTERATION) {
                            // Validate the thing that was added or altered
                            const childMeta = meta.fields[key];
                            if (!alreadyValidated.has(childMeta)) {
                                meta.fields[key].validate(newSubValue[key]);
                            }
                        }
                    }
                }
            }
        }

        return {
            formValue: newValue || currentState.formValue,
            formMeta: currentState.formMeta,
            updateForm: currentState.updateForm,
        };
    }, []);

    const [state, dispatch] = useReducer(reducer, { formValue: value, formMeta, updateForm });

    function update(path?: Path, newValue?) {
        if (path) {
            dispatch({ path, value: newValue });
        } else {
            updateForm();
        }
    }

    function updateForm(newValue?: T) {
        if (newValue) {
            // For multiple updateForm calls with a new value in the same cycle,
            // use the most recent. (Or have a call with a new value supercede
            // previous calls that did not have a new value.)
            if (scheduledDispatch.current) {
                window.clearTimeout(scheduledDispatch.current);
            }
            const sd = window.setTimeout(dispatch, 0, { value: newValue });
            scheduledDispatch.current = sd;
        } else if (!scheduledDispatch.current) {
            // All updateForm calls *without* a value are the same, so no need
            // to do any superceding; if there's a dispatch scheduled already,
            // then great, that's what we needed. Otherwise get one set up.
            const sd = window.setTimeout(dispatch, 0, {});
            scheduledDispatch.current = sd;
        }
    }

    return state;
}
